import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getAdmissionEvents, patchAdmission } from '../../services/api/admissionApi';
import { getPatient } from '../../services/api/visitApi';
import AbhaBadge from '../visit/AbhaBadge';
import LinkedRecordsList from '../abha/LinkedRecordsList';
import { maskAbhaNumber, maskAbhaAddress } from '../../services/api/abhaProfile';
import { getRunningBill } from '../../services/api/admissionChargesApi';
import { collectDeposit, returnAdvance, writeOffVoucherUrl } from '../../services/api/admissionPaymentsApi';
import {
  initiateDischarge,
  cancelInitiation,
  fixChargesAfterDischargeDate,
  clearBilling,
  completeDischarge,
  openInterimInvoicePdf,
  openDischargeSummaryPdf,
  duesAckPdfUrl,
  putDeathRecord,
  deleteDeathRecord,
} from '../../services/api/admissionDischargeApi';
import { fetchStaffList } from '../../services/api/staffApi';
import { usePermission, useScope } from '../../context/PermissionContext';
import useDiagnosticsBillPrint from '../../hooks/useDiagnosticsBillPrint';
import {
  ADMISSION_STATUS_LABELS,
  CHARGE_PERMISSIONS,
  PAYMENT_PERMISSIONS,
  ADMISSION_PERMISSIONS,
  DISCHARGE_PERMISSIONS,
  DUES_PERMISSIONS,
  PAYER_TYPE_LABELS,
  ADMISSION_EVENT_KIND,
  isDepositCollectable,
  isAwaitingBed,
  isCancelable,
  isDischargeStartable,
  isInDischargeFlow,
  hasFinalBill,
  isTerminal,
  isInvoiceBearing,
  ADMISSION_STATUS,
  isDeathOutcome,
  hasOpenDeathRecord,
  buildDeclaringDoctorOptions,
  DECEASED_FROM_STATUSES,
  canPlanDischarge,
  PAYER_MODE,
  isMoneyHidden,
  ADMISSION_ACCESS_ERROR_CODE,
  BILL_HINT_DOCTOR_COPY,
  BILL_HINT_NURSE_COPY,
  NOT_TREATING_DOCTOR_DISCHARGE_COPY,
  CANCEL_INITIATION_ADMIN_ONLY_COPY,
} from '../../constants/admission';
import RunningBillPanel from './bill/RunningBillPanel';
import TpaBillSection from './bill/TpaBillSection';
import DiagnosticWaiverDialog from './bill/DiagnosticWaiverDialog';
import DiagnosticsCard from './diagnostics/DiagnosticsCard';
import { buildAdmissionKpis, buildRefundableDeposits } from './bill/billModel';
import { describeDuesState } from './duesState';
import { deriveTaskbar } from './admissionWorkspaceModel';
import {
  buildAuthoriserOptions,
  buildDodHistory,
  dodEditsFromEvents,
  dodBackdatedLine,
  currentDodEdit,
  DOD_NOT_EDITABLE_ERROR,
} from './discharge/dischargeModel';
import { formatPaise } from '../../utils/money';
import { formatDischargeDateTimeIST } from './discharge/planDischargeModel';
import DischargeDialog from './discharge/DischargeDialog';
import DischargeSummaryPanel from './discharge/DischargeSummaryPanel';
import PayerInsuranceCard from './PayerInsuranceCard';
import TreatingDoctorsCard, { NonTreatingDoctorGate } from './TreatingDoctorsCard';
import { OutOfDepartmentText } from '../common/NoAccessPanel';
import ViewerRelationshipChip from './ViewerRelationshipChip';
import { treatingDoctorLabel } from './deskFilters';
import { describeInsuranceEventChanges } from './insuranceModel';
import { describeDeathRecordEventChanges } from './deathRecordModel';
import RecordDeathDialog from './RecordDeathDialog';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import PlanDischargeDialog from './discharge/PlanDischargeDialog';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';
import { formatAddressLines } from '../../utils/patientAddress';
import { patientAgeDisplay } from '../../utils/ageValue';
import CancelInitiationDialog from './discharge/CancelInitiationDialog';
import {
  DISCHARGE_PLAN_CLEARED_TOAST,
  plannedDischargeChipText,
  dischargeDateRow,
  planDischargeErrorMessage,
} from './discharge/planDischargeModel';


/**
 * invoice-print-record — Cancel initiation after the invoice was printed before
 * Complete is ADMIN-only. The role is read HERE only (mounted solely when the
 * flag is set), so no other workspace row reads it.
 */
function AdminOnlyAfterPrintGate({ children }) {
  const { isAdmin } = useScope();
  return children(!isAdmin);
}

/**
 * AdmissionWorkspace (UX-1 · Variant B) — the right-hand pane, pinned to the
 * admission selected in the left rail. The pinned top (KPI strip · patient
 * header · ONE task bar · tab bar) never scrolls away; the Bill / Timeline /
 * Details tabs scroll underneath.
 *
 * This ONE task bar replaces Variant A's three duplicated action rows: its
 * contents DERIVE from the admission status via the pure `deriveTaskbar`
 * selector (single source), gated by the viewer's permissions. Bill-line
 * actions (+Add charge / Reverse / Refund / Invoice) stay in the Bill tab
 * (`RunningBillPanel embedded`) — a different scope, not a duplicate.
 *
 * Mounted with `key={admission_id}` by the desk, so switching rail selection
 * remounts it — tab/timeline/discharge state resets cleanly per admission with
 * no stale carry-over and no extra guards.
 */
const chipCls = 'text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap';
const btn = {
  default: 'px-2.5 py-1.5 text-[12.5px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700',
  primary: 'px-2.5 py-1.5 text-[12.5px] rounded-md bg-teal-600 text-white font-semibold hover:bg-teal-700',
  danger: 'px-2.5 py-1.5 text-[12.5px] rounded-md border border-red-300 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left',
  writeoff: 'px-2.5 py-1.5 text-[12.5px] rounded-md border border-dashed border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-700',
};

function Chip({ text, tone }) {
  return <span className={`${chipCls} ${tone}`}>{text}</span>;
}

function Kpi({ label, value, sub, tone }) {
  const valueTone = tone === 'owe' ? 'text-red-700 dark:text-red-300' : tone === 'advance' ? 'text-teal-700 dark:text-teal-300' : 'text-slate-900 dark:text-white';
  return (
    <div className="flex-1 min-w-[120px] rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2">
      <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
      <div className={`text-[17px] font-extrabold leading-tight tabular-nums ${valueTone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{sub}</div>}
    </div>
  );
}

// ── Details tab grid — ported from Variant A's DetailGrid ──────────────────
function DetailGrid({
  admission, events, eventsError, duesState, canEditInsurance, onInsuranceSaved, patientDetailsRefreshToken,
  careTeamCard, dischargeDateRowValue, dodHistory, dodEdits, currentDodEdit: currentEdit, onExpandDodHistory,
  canManagePlanDischarge,
  onChangePlanDischarge, onClearPlanDischarge,
}) {
  const a = admission;
  // K7 — dod_edit of the CURRENT initiation only (after the latest cancel); null when not backdated.
  const backdatedLine = dodBackdatedLine(currentEdit);
  // admission-access — money withheld: no Dues block, no estimate amount, no
  // TPA bills (the server refuses that read); expected stay days stay.
  const moneyHidden = isMoneyHidden(a);
  const [clearBusy, setClearBusy] = useState(false);
  const [clearError, setClearError] = useState(null);
  const runClearPlanDischarge = useCallback(async () => {
    if (clearBusy) return;
    setClearBusy(true);
    setClearError(null);
    const res = await onClearPlanDischarge();
    setClearBusy(false);
    if (!res.success) setClearError(planDischargeErrorMessage(res));
  }, [clearBusy, onClearPlanDischarge]);
  const attender = a.attender || null;
  const estimate = a.estimate || null;
  const bedLine = a.bed_label
    ? `Bed: ${a.bed_label}${a.ward_name ? ` · ${a.ward_name}` : ''}`
    : (isAwaitingBed(a) ? 'Awaiting bed' : 'No bed assigned');
  const label = 'text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5';
  const line = 'text-[13px] text-slate-700 dark:text-slate-200 mb-1';
  const empty = 'text-[13px] italic text-slate-400 dark:text-slate-500';

  // Address isn't denormalized onto the admission row (unlike patient_name) —
  // fetched here, only while the Details tab is actually mounted, via the
  // dedicated GET /patients/{id} (never stale: no copy to keep in sync).
  // The structured address shows as TWO lines (`formatAddressLines`, the FE
  // mirror of the server formatter; legacy one-liner only when all six are
  // empty). AGE-FE (`AGE-EDIT-spec.md` §4) / OR-127 — age rides the SAME
  // fetch, no second request: `patientAgeDisplay` (mirror of the server's
  // `patient_age_display`) = derived from DOB when one is known, else the
  // stored age. A blank age renders nothing, same "omit, never a fake dash"
  // posture as the address line above.
  //
  // D1 fix (QA `qa-report-slice1.md`) — `patientDetailsRefreshToken` (the
  // SAME monotonic counter `DischargeSummaryPanel` already keys its own
  // patient re-fetch on, AGE-FE/bk-1292) joins the dep array so a save on
  // the sibling "Patient Details" dialog re-fires this GET exactly once —
  // before this, the effect only re-ran when `a.patient_id` changed, so the
  // Details tab kept showing the pre-edit age/address until the admission
  // was switched. `cancelled` (unchanged) still drops a stale in-flight
  // response if the admission is switched mid-fetch.
  const [patientAddress, setPatientAddress] = useState(undefined);
  const [patientAge, setPatientAge] = useState(undefined);
  // IP-1 (R42): ABHA fields ride the same getPatient fetch — no second request.
  const { show: abdmOn } = useAbdmEnabled();
  const [patientAbha, setPatientAbha] = useState(null);
  const [linkedShown, setLinkedShown] = useState(false); // R130: full linked box replaces the masked lines
  useEffect(() => {
    let cancelled = false;
    setPatientAddress(undefined);
    setPatientAge(undefined);
    setPatientAbha(null);
    if (a.patient_id) {
      getPatient(a.patient_id)
        .then((p) => {
          if (cancelled) return;
          const lines = formatAddressLines(p);
          setPatientAddress(lines ? [lines.line1, lines.line2].filter(Boolean).join('\n') : null);
          setPatientAge(patientAgeDisplay(p) || null);
          setPatientAbha(p ? { status: p.abha_status, number: p.abha_number, address: p.abha_address } : null);
        })
        .catch(() => {
          if (cancelled) return;
          setPatientAddress(null);
          setPatientAge(null);
        });
    }
    return () => { cancelled = true; };
  }, [a.patient_id, patientDetailsRefreshToken]);

  return (
    <div className="p-4">
      <div className="grid gap-4 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
      <div>
        <h4 className={label}>Patient</h4>
        <div className={line}><b className="text-slate-900 dark:text-white">{a.patient_name || a.patient_id}</b></div>
        {/* FIX-14 — a read-only MIRROR of the card's payer-type selector below
            (one editor, one source): it re-reads whatever the last save
            response wrote, never a second control. */}
        <div className={line} data-testid="details-payer-type">{a.ip_number} · {PAYER_TYPE_LABELS[a.payer_type] || a.payer_type || 'No payer set'}</div>
        {patientAge && <div className={line} data-testid="admission-workspace-patient-age">Age: {patientAge}</div>}
        {patientAddress && <div className={`${line} whitespace-pre-line`} data-testid="admission-workspace-patient-address">{patientAddress}</div>}
        <div className={line}>{a.is_mlc ? 'MLC case' : 'Not an MLC case'}</div>
        {a.is_mlc && (
          <>
            {a.mlc_number && <div className={line}>MLC No: {a.mlc_number}</div>}
            {a.police_station && <div className={line}>Police station: {a.police_station}</div>}
            {a.police_informed_at && (
              <div className={line}>Police informed: {new Date(a.police_informed_at).toLocaleString()}</div>
            )}
          </>
        )}
      </div>
      {abdmOn && patientAbha && (
        <div data-testid="admission-workspace-abha">
          <h4 className={label}>ABHA</h4>
          <div className={line}><AbhaBadge status={patientAbha.status} isDark={document.documentElement.classList.contains('dark')} /></div>
          {!linkedShown && patientAbha.number && <div className={line} data-testid="admission-workspace-abha-number">{maskAbhaNumber(patientAbha.number)}</div>}
          {!linkedShown && patientAbha.address && <div className={line}>{maskAbhaAddress(patientAbha.address)}</div>}
          {patientAbha.number && (
            <div className="mt-2">
              <LinkedRecordsList patientId={a.patient_id} isDark={document.documentElement.classList.contains('dark')} onShowingChange={setLinkedShown} />
            </div>
          )}
        </div>
      )}
      <div>
        <h4 className={label}>Care &amp; bed</h4>
        <div className={line}>Primary doctor: <b className="text-slate-900 dark:text-white">{a.primary_doctor_name || 'No doctor assigned'}</b></div>
        <div className={line}>Department: <b className="text-slate-900 dark:text-white">{a.department_name || 'No department'}</b></div>
        <div className={line}>{bedLine}</div>
        <div className={line}>{a.admitted_at ? `Admitted ${new Date(a.admitted_at).toLocaleString()}` : 'Not yet admitted'}</div>
      </div>
      <div>
        <h4 className={label}>Attender</h4>
        {attender && attender.name
          ? (
            <>
              <div className={line}><b className="text-slate-900 dark:text-white">{attender.name}</b>{attender.relation ? ` (${attender.relation})` : ''}</div>
              {(attender.phone || attender.mobile) && <div className={line}>{attender.phone || attender.mobile}</div>}
            </>
          )
          : <div className={empty}>No attender recorded</div>}
      </div>
      {!moneyHidden && duesState.kind !== 'NONE' && (
        <div data-testid={`dues-detail-${a.admission_id}`}>
          <h4 className={label}>Dues</h4>
          {duesState.detailLines.map((text, i) => (
            <div key={i} className={line}>{i === 0 ? <b className="text-slate-900 dark:text-white">{text}</b> : text}</div>
          ))}
        </div>
      )}
      {/* Money hidden: only the expected stay days survive, and only when the
          server sent them — "No estimate set" would be a false claim about a
          withheld estimate, so the cell is absent instead. */}
      {(!moneyHidden || (estimate && estimate.expected_stay_days != null)) && (
        <div data-testid="details-plan-estimate">
          <h4 className={label}>Plan &amp; estimate</h4>
          {estimate
            ? (
              <>
                {estimate.expected_stay_days != null && <div className={line}>Expected stay: {estimate.expected_stay_days} day(s)</div>}
                {!moneyHidden && estimate.grand_total != null && <div className={line}>Estimate: <b className="text-slate-900 dark:text-white">₹{Number(estimate.grand_total).toLocaleString('en-IN')}</b></div>}
              </>
            )
            : <div className={empty}>No estimate set</div>}
        </div>
      )}
      {/* Owner ruling (FIX-6-FE) — "if discharge is planned it should display
          the date when clicking on Details." Reuses the SAME
          `plannedDischargeChipText`/`plannedDischargeChipVisible` family the
          header chip and desk-row chip already read (single source) — absent
          when unplanned, hidden after completion, no second formatter. */}
      {/* DOD rule v3 / Screen B — ONE row, three states off the ONE
          `dischargeDateRow` selector (`planDischargeModel`, the same family
          the four chips read, so the row can never name a different date):
            · pre-initiate  → "Planned discharge: …" + Change plan / Clear plan
            · post-initiate → "Discharge date: …" + Change ONLY (a confirmed
              date cannot be emptied — Clear is HIDDEN, not disabled)
            · post-complete → read-only + the correction-rail note. */}
      {dischargeDateRowValue && (
        <div data-testid="details-planned-discharge">
          <h4 className={label}>{dischargeDateRowValue.heading}</h4>
          <div className={line}>{dischargeDateRowValue.text}</div>
          {dischargeDateRowValue.completed ? (
            <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1" data-testid="details-discharge-date-completed-note">
              {DOD_NOT_EDITABLE_ERROR}
            </div>
          ) : dischargeDateRowValue.confirmed ? (
            backdatedLine && (
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1" data-testid="details-backdated-line">
                {backdatedLine}
              </div>
            )
          ) : canManagePlanDischarge && (
            <div className="flex items-center gap-3 mt-1">
              <button
                type="button"
                onClick={onChangePlanDischarge}
                className="text-[12px] font-semibold text-teal-700 dark:text-teal-300 underline"
                data-testid="details-change-plan-discharge"
              >
                Change plan
              </button>
              <button
                type="button"
                onClick={runClearPlanDischarge}
                disabled={clearBusy}
                className="text-[12px] font-semibold text-red-700 dark:text-red-300 underline disabled:opacity-60"
                data-testid="details-clear-plan-discharge"
              >
                {clearBusy ? 'Clearing…' : 'Clear plan'}
              </button>
            </div>
          )}
          {/* S2 AC3 — the change history, newest first, first 3 + "+n more";
              ABSENT entirely when the date was never edited. Same
              `buildDodHistory` rows the Change dialog lists. */}
          {dodHistory.rows.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-slate-500 dark:text-slate-400" data-testid="details-dod-history">
              {dodHistory.rows.map((r) => <li key={r.key}>{r.text}</li>)}
              {dodHistory.overflowLabel && (
                <li>
                  <button
                    type="button"
                    onClick={onExpandDodHistory}
                    className="text-[11.5px] font-semibold text-teal-700 dark:text-teal-300 hover:underline"
                    data-testid="details-dod-history-overflow"
                  >{dodHistory.overflowLabel}</button>
                </li>
              )}
            </ul>
          )}
          {clearError && (
            <div className="text-[11.5px] text-red-600 dark:text-red-300 mt-1" data-testid="details-clear-plan-discharge-error">
              {clearError}
            </div>
          )}
        </div>
      )}
      {eventsError && <div className="text-[12px] text-red-600 dark:text-red-300">{eventsError}</div>}
      </div>

      {/* admission-access M-FE-1 — Treating doctors (primary + added), built by
          the workspace (null on an older server that sends no `added_doctors`). */}
      {careTeamCard}

      {/* Wave-3 FE-1 — Payer & insurance capture card (mock1), next to the
          existing Attender/Plan & estimate cells above. */}
      <PayerInsuranceCard admission={a} canEdit={canEditInsurance} onSaved={onInsuranceSaved} />

      {/* TPA-PREAUTH/tpa-bill S1 (FE-2, stories.md Screen C) — the issued
          TPA bills list, beneath the insurance card. Mounted only for a
          CASHLESS admission (AC2) — a non-cashless admission never issues
          the `GET .../tpa-bills` request at all, and the section itself is
          additionally ABSENT until a first bill exists. */}
      {a.payer_mode === PAYER_MODE.CASHLESS && !moneyHidden && (
        <TpaBillSection admissionId={a.admission_id} />
      )}
    </div>
  );
}

export default function AdmissionWorkspace({
  admission, onAction, onDischarged, onAdmissionPatched, onClose, maximized, onMaximize, onBackToSplit,
  diagnosticsRefreshToken, patientDetailsRefreshToken,
}) {
  const a = admission;
  const status = a.status;
  const admissionId = a.admission_id;

  // ── Permissions (contract-frozen slugs — single source, never inline) ────
  const canViewBill = usePermission(CHARGE_PERMISSIONS.VIEW_RUNNING_BILL);
  // Add charge (relocated onto the taskbar) — the SAME slug the Bill-tab
  // panel's own "+ Add charge" gates on (CHARGE_PERMISSIONS.ADD_CHARGE).
  const canAddCharge = usePermission(CHARGE_PERMISSIONS.ADD_CHARGE);
  const canCollectDeposit = usePermission(PAYMENT_PERMISSIONS.COLLECT_DEPOSIT) && isDepositCollectable(status);
  const canGrantConcession = usePermission(CHARGE_PERMISSIONS.GRANT_CONCESSION) && !!a.ip_number;
  const canTransferBed = usePermission(ADMISSION_PERMISSIONS.TRANSFER_BED);
  // Owner ruling — "no lab should be allowed to enter after discharge is
  // initiated." Same shared `isInvoiceBearing` gate DiagnosticsCard applies
  // to its own "+ Order Diagnostic" button (never re-derived) — this taskbar action
  // is a SECOND entry point into the SAME OrderLabDialog and needs the same
  // guard so it can't route around it.
  const canOrderLab = usePermission(ADMISSION_PERMISSIONS.ORDER_ADMISSION_LAB) && !isInvoiceBearing(status);
  const canDischargePerm = usePermission(DISCHARGE_PERMISSIONS.DISCHARGE_PATIENT);
  const canDischarge = canDischargePerm && (isDischargeStartable(status) || isInDischargeFlow(status));
  const canCollectDues = usePermission(DUES_PERMISSIONS.COLLECT_DUES);
  const canWriteOffDues = usePermission(DUES_PERMISSIONS.WRITE_OFF_DUES);
  const canPrintWriteoffVoucher = usePermission(PAYMENT_PERMISSIONS.GENERATE_RECEIPT_PDF);
  // Discharge dialog perms — R3-4/S4: the write-off exception at discharge
  // reuses the SAME WRITE_OFF_DUES slug as the Payment Pending queue (no new
  // permission), so `canWriteOffDues` (already computed above) is shared.
  // Owner redesign — the discharge "Return advance" is the BOUNDED return, gated
  // on RETURN_ADVANCE (reception + admin). The free-form per-row "Refund deposit"
  // (REFUND_DEPOSIT, admin only) lives inside RunningBillPanel, which computes
  // its own canRefund — this workspace only wires the discharge return.
  const canReturnAdvance = usePermission(PAYMENT_PERMISSIONS.RETURN_ADVANCE);
  const canPrintDischargeDocs = usePermission(DISCHARGE_PERMISSIONS.GENERATE_IPD_BILL_PDF);
  // Wave-3 FE-1 — Payer & insurance capture card. The dedicated insurance
  // PATCH (/admissions/{id}/insurance, C-1) is gated ADMIT_PATIENT OR
  // COLLECT_DEPOSIT server-side so the biller can enter the insurer money
  // fields; mirror BOTH here (hooks called unconditionally) or billers see the
  // card read-only.
  // Raw COLLECT_DEPOSIT here (NOT the window-gated `canCollectDeposit` above) —
  // a biller may capture insurer details any time, not only while a deposit is
  // collectable.
  const canAdmitPatient = usePermission(ADMISSION_PERMISSIONS.ADMIT_PATIENT);
  // bk-1710 — Cancel admission rides the transition route's CANCEL_ADMISSION
  // slug (same pattern as ADMIT_PATIENT above): never offered to a role the
  // server refuses (DOCTOR / NURSE).
  const canCancelAdmission = usePermission(ADMISSION_PERMISSIONS.CANCEL_ADMISSION);
  const hasCollectDepositPerm = usePermission(PAYMENT_PERMISSIONS.COLLECT_DEPOSIT);
  const canEditInsurance = canAdmitPatient || hasCollectDepositPerm;
  // S4 Plan Discharge (C2 owner ruling 4) — OR-gated setter: ADMIT_PATIENT OR
  // DISCHARGE_PATIENT (both already resolved above — `canAdmitPatient`,
  // `canDischargePerm` — reused rather than a third `usePermission` call for
  // the SAME two slugs). Hidden (never 409-on-click) without either; hidden
  // once the admission is past a pre-completion status (AC-NEG/AC3).
  const canPlanDischargeNow = (canAdmitPatient || canDischargePerm) && canPlanDischarge(status);
  // S2 / ruling 5 — the CONFIRMED date is changed by the same permission that
  // may complete the discharge (DISCHARGE_PATIENT); >48 h back is the ladder's
  // ADMIN rung, inside the dialog. A ward doctor/nurse without it sees the row
  // read-only, with no Change affordance (AC-NEG) — never a 409-on-click.

  // admission-access — `money_visible === false`: the server withheld every
  // money field. The Bill tab, the money KPI cards and every running-bill
  // request go away (never ₹0); `undefined` (older server) = today's behaviour.
  const moneyHidden = isMoneyHidden(a);
  // The strip also shows for a viewer WITHOUT the bill slug when the server
  // flagged the money hidden (nurse): bed + day of stay only, so the header does
  // not jump between roles. `undefined` + no slug = today's behaviour (no strip).
  const showKpiStrip = (canViewBill || moneyHidden) && !!a.ip_number;
  const showBill = showKpiStrip && !moneyHidden;
  // Discharge-summary authoring eligibility — single source, shared by the
  // tab-bar label/enable and the tab's own content branch below (#9 follow-up:
  // the panel previously existed but was undiscoverable, buried inside the
  // generic "Details" tab with no label pointing to it — see the "Discharge
  // Summary" tab render for the fix).
  const showDischargeSummary = (!isTerminal(status) || hasFinalBill(status)) && !!a.ip_number && status !== ADMISSION_STATUS.CANCELLED;
  const kpi = buildAdmissionKpis(a);
  const duesState = describeDuesState(a);

  const caps = {
    canAddCharge,
    canTransferBed,
    canOrderLab,
    canCollectDeposit,
    canGrantConcession,
    canDischarge,
    dischargeResume: isInDischargeFlow(status),
    canCancelInitiation: canDischargePerm && isInDischargeFlow(status),
    canCancel: canCancelAdmission && isCancelable(status),
    canCollectDues,
    canWriteOffDues,
    canPrintWriteoffVoucher,
    canAdmitPatient,
    moneyHidden,
  };
  // deriveTaskbar is a cheap pure selector — recomputing per render is fine and
  // avoids an unstable-deps memo over the freshly-built `caps` object.
  const taskbar = deriveTaskbar(a, caps);
  // "Assign doctor" (summary tab + discharge dialog banners) opens the same
  // Change doctor dialog; its route needs ADMIT_PATIENT — offered to holders only.
  const assignDoctor = canAdmitPatient ? () => onAction('changeDoctor', a) : undefined;
  // admission-access M-FE-1 — Treating doctors card on Details. Absent when the
  // server sent no `added_doctors` (older backend = today's behaviour). Writes
  // reload the admission through the desk's own refresh (`onDischarged`), so the
  // row stays the single source — no local copy of the care team here.
  const careTeamCard = Array.isArray(a.added_doctors) ? (
    <TreatingDoctorsCard
      admission={a}
      canAdmitPatient={canAdmitPatient}
      onChangePrimary={assignDoctor}
      onChanged={onDischarged}
    />
  ) : null;

  // ── Death record (FE-2, death-record slice) — the quiet header link +
  // banner. EP-D1/EP-D2 both return the FULL admission (S5); this component
  // keeps a small LOCAL override of just the two fields that change
  // (`cause_of_death`, `death_rev`) so the banner re-renders off the SAME
  // response with no refetch — the identical pattern DischargeDialog already
  // uses for its own `currentStatus`/`invoiceNumberPrinted`. Everything else
  // about the admission (bed, ward, patient identity) is unaffected by a
  // death record (invariant 7), so merging onto `a` is safe.
  const [deathOverride, setDeathOverride] = useState(null);
  const deathAdmission = deathOverride ? { ...a, ...deathOverride } : a;
  const canRecordDeath = canDischargePerm && DECEASED_FROM_STATUSES.has(status);
  const [recordDeathOpen, setRecordDeathOpen] = useState(false);
  const [declaringDoctorOptions, setDeclaringDoctorOptions] = useState([]);
  const doctorRosterFetchedRef = useRef(false);
  useEffect(() => {
    if (!recordDeathOpen || doctorRosterFetchedRef.current) return;
    doctorRosterFetchedRef.current = true;
    // fetchStaffList is single-flight + session-cached (staffApi) — this
    // cannot multiply requests alongside the discharge dialog's OWN roster
    // fetch above; DOCTOR-only filtering happens HERE (buildDeclaringDoctorOptions,
    // constants/admission.js), never in dischargeModel.js's role-agnostic filter.
    let alive = true;
    fetchStaffList().then((res) => {
      if (alive && res.success) setDeclaringDoctorOptions(buildDeclaringDoctorOptions(res.data));
    });
    return () => { alive = false; };
  }, [recordDeathOpen]);
  const applyDeathResponse = useCallback((fullAdmission) => {
    setDeathOverride({ cause_of_death: fullAdmission.cause_of_death, death_rev: fullAdmission.death_rev });
  }, []);
  const handleSaveDeathRecord = useCallback((body) => putDeathRecord(admissionId, body), [admissionId]);
  const openRecordDeath = useCallback(() => setRecordDeathOpen(true), []);
  const closeRecordDeath = useCallback(() => setRecordDeathOpen(false), []);

  const [removeDeathConfirming, setRemoveDeathConfirming] = useState(false);
  const [removeDeathBusy, setRemoveDeathBusy] = useState(false);
  const [removeDeathError, setRemoveDeathError] = useState(null);
  const runRemoveDeathRecord = useCallback(async () => {
    if (removeDeathBusy) return;
    setRemoveDeathBusy(true);
    setRemoveDeathError(null);
    const res = await deleteDeathRecord(admissionId, deathAdmission.death_rev || 0);
    setRemoveDeathBusy(false);
    if (res.success) {
      applyDeathResponse(res.data.admission);
      setRemoveDeathConfirming(false);
    } else {
      // [NEEDS-PO]
      setRemoveDeathError(res.error || 'That action could not be completed.');
    }
  }, [removeDeathBusy, admissionId, deathAdmission.death_rev, applyDeathResponse]);

  // ── Payer linkage (FIX-14) — `payer_type` and `payer_mode` are linked and
  // the derivation is SERVER-owned (build-plan §C8). Whichever field the desk
  // saves on the Payer & insurance card, the response carries BOTH, so the
  // card's own header chip and the Details payer-type mirror move together
  // off that ONE body — a local merge (the same `deathOverride` idiom above),
  // never a refetch: this handler fires ZERO requests of its own. The desk row
  // gets the same two keys through the existing `patchAdmissionBalance`
  // whitelist so the rail/export can't disagree either. Stable via
  // `useCallback` on primitives, so it can never re-trigger a child effect.
  const [payerOverride, setPayerOverride] = useState(null);
  const handleInsuranceSaved = useCallback((fullAdmission) => {
    const next = {};
    if (fullAdmission && fullAdmission.payer_type != null) next.payer_type = fullAdmission.payer_type;
    if (fullAdmission && fullAdmission.payer_mode != null) next.payer_mode = fullAdmission.payer_mode;
    if (Object.keys(next).length) {
      setPayerOverride((cur) => ({ ...cur, ...next }));
      if (typeof onAdmissionPatched === 'function') {
        onAdmissionPatched({ admission_id: admissionId, ...next });
      }
    }
    // The card's OTHER effects (an insurer-money reversal moves the balance)
    // still ride the parent's existing refresh — unchanged behaviour.
    if (typeof onDischarged === 'function') onDischarged();
  }, [admissionId, onAdmissionPatched, onDischarged]);
  // The admission the Details tab renders from — the prop with the payer
  // fields' last-saved values merged over it (both DetailGrid mounts, split
  // and maximized, read this ONE value).
  const payerAdmission = payerOverride ? { ...a, ...payerOverride } : a;

  // ── Plan discharge (S4/FE-2) — own dialog, own PATCH via the general
  // `patchAdmission` (C2 — the SAME route the Payer & attender panels already
  // write through, body-scoped to `planned_discharge_at` only, never a
  // second endpoint). `plannedDischargeOverride` is the identical local-merge
  // idiom `deathOverride` above uses: `undefined` = "no local override yet,
  // read off the prop"; `null` is a LEGITIMATE override value (a just-cleared
  // plan), so the sentinel must not collapse the two. ───────────────────────
  const [planDischargeOpen, setPlanDischargeOpen] = useState(false);
  const [plannedDischargeOverride, setPlannedDischargeOverride] = useState(undefined);
  // DOD rule v3 — the CONFIRMED-date marker rides the same local-override
  // idiom as the date beside it, so an Initiate flips every affordance on
  // this screen in the SAME render (Screen A: "local merge, no refetch").
  const [dodConfirmedOverride, setDodConfirmedOverride] = useState(undefined);
  let planDischargeAdmission = a;
  if (plannedDischargeOverride !== undefined) {
    planDischargeAdmission = { ...planDischargeAdmission, planned_discharge_at: plannedDischargeOverride };
  }
  if (dodConfirmedOverride !== undefined) {
    planDischargeAdmission = { ...planDischargeAdmission, discharge_date_confirmed_at: dodConfirmedOverride };
  }
  // FE-3 (build-plan/stories Screen A header, owner ruling 5) — read-only
  // header chip, SAME selector as the desk-row chip (AdmissionRailItem) and
  // FE-4's running-bill chip. Reads off `planDischargeAdmission` (the local
  // override above) so a save/clear updates the chip immediately with no
  // refetch; '' (unset/unparseable, OR the stay is discharged/terminal — D3)
  // renders no chip — never an empty one.
  const plannedDischargeChipTextValue = plannedDischargeChipText(planDischargeAdmission);
  // Screen B — the Details row's own three-state line (same selector family,
  // same merged admission, so the row and the chip can never disagree).
  const dischargeDateRowValue = dischargeDateRow(planDischargeAdmission);
  const [planDischargeToast, setPlanDischargeToast] = useState(null);
  const openPlanDischarge = useCallback(() => setPlanDischargeOpen(true), []);
  const closePlanDischarge = useCallback(() => setPlanDischargeOpen(false), []);
  const handleSavePlanDischarge = useCallback((body) => patchAdmission(admissionId, body), [admissionId]);
  const applyPlanDischargeResponse = useCallback((fullAdmission) => {
    const next = fullAdmission ? fullAdmission.planned_discharge_at ?? null : null;
    // DOD rule v3 (C4) — the marker travels WITH the date, on this ONE merge
    // path (plan save/clear · initiate · confirmed-date edit), so no surface
    // can end up with the new date under the old label.
    const marker = fullAdmission ? fullAdmission.discharge_date_confirmed_at ?? null : null;
    setPlannedDischargeOverride(next);
    setDodConfirmedOverride(marker);
    // The desk-side merge (`patchAdmissionBalance`, deskFilters) passes both
    // keys through by name, so the desk row re-labels itself with NO refetch.
    if (typeof onAdmissionPatched === 'function') {
      onAdmissionPatched({
        admission_id: admissionId,
        planned_discharge_at: next,
        discharge_date_confirmed_at: marker,
      });
    }
  }, [admissionId, onAdmissionPatched]);
  const handlePlanDischargeSaved = useCallback((fullAdmission) => {
    applyPlanDischargeResponse(fullAdmission);
  }, [applyPlanDischargeResponse]);
  const handlePlanDischargeCleared = useCallback((fullAdmission) => {
    applyPlanDischargeResponse(fullAdmission);
    // AC-I5 — ratified toast, transient banner idiom (setTimeout auto-dismiss)
    // this domain already uses elsewhere (DischargeSummaryPanel's snippet
    // toast) rather than a new shared Toast component nothing else needs.
    setPlanDischargeToast(DISCHARGE_PLAN_CLEARED_TOAST);
    setTimeout(() => setPlanDischargeToast(null), 3000);
  }, [applyPlanDischargeResponse]);
  // FIX-6-FE (owner ruling) — the Details tab's own "Clear plan" shortcut
  // reuses the dialog's EXACT clear path: same `handleSavePlanDischarge` PATCH
  // (no new endpoint, no new fetch) and the same `handlePlanDischargeCleared`
  // success handler (local override + AC-I5 toast) the dialog's own Clear
  // plan button drives — this just skips opening the modal first.
  const handleClearPlanDischargeFromDetails = useCallback(async () => {
    const res = await handleSavePlanDischarge({ planned_discharge_at: null });
    if (res.success) handlePlanDischargeCleared(res.data);
    return res;
  }, [handleSavePlanDischarge, handlePlanDischargeCleared]);

  // No Bill tab for a money-hidden viewer: they land on the Discharge Summary
  // (Timeline when the summary isn't available yet). `rawTab` keeps whatever
  // was asked for; a stale 'bill' (initial state, maximize fallback, or the
  // row flipping to hidden while open) resolves to the home tab at render.
  const homeTab = moneyHidden ? (showDischargeSummary ? 'summary' : 'timeline') : 'bill';
  const [rawTab, setTab] = useState('bill');
  const tab = moneyHidden && rawTab === 'bill' ? homeTab : rawTab;
  const [moreOpen, setMoreOpen] = useState(false);
  // Add-charge trigger — the AddChargeDialog itself stays owned by
  // RunningBillPanel (single home, no second dialog instance here); this
  // token is the lifted "open it" signal the taskbar bumps, forwarded down
  // as a prop so RunningBillPanel can open its existing 'add' dialog state.
  const [addChargeToken, setAddChargeToken] = useState(0);
  // DIAG-DISCOUNT v5 FE-G — the task-bar's OWN DiagnosticWaiverDialog mount
  // (peer to "Waiver / concession") sits OUTSIDE RunningBillPanel and
  // DiagnosticsCard, so recording a waiver there needs its own "please
  // refresh" signal — bumped on `onRecorded`, folded into the SAME
  // `diagnosticsRefreshToken` both of those already watch (a STRING, still a
  // primitive — safe as an effect dep either way) rather than inventing a
  // second refresh channel.
  const [waiverRecordToken, setWaiverRecordToken] = useState(0);
  const diagnosticsRefreshSignal = `${diagnosticsRefreshToken ?? 0}:${waiverRecordToken}`;

  // S3/FE-B — maximized mode drops "Details" as a tab (it becomes an
  // always-visible side column instead). If the viewer was already ON that
  // tab when maximizing, fall back to Bill rather than leave a dangling tab.
  useEffect(() => {
    if (maximized && tab === 'details') setTab('bill');
  }, [maximized, tab]);

  // ── Timeline: fetch ONCE on first Timeline-tab open (ref-guarded single-
  //    flight, keyed on the primitive admission id via the remount). ─────────
  const [events, setEvents] = useState(null);
  const [eventsError, setEventsError] = useState(null);
  // admission-access B — the server refused this admission's data for the
  // viewer's department: the whole workspace gives way to the panel.
  const [outOfScope, setOutOfScope] = useState(false);
  const eventsFetchedRef = useRef(false);
  // S2 AC3 — the Details tab now READS this same list (the confirmed-date
  // change history rides `ADMISSION_DETAILS_UPDATED.refs.dod_edit`), and the
  // maximized layout shows Details permanently. Still ONE fetch per mount:
  // the ref guard below is unchanged, and every dep here is a primitive.
  const wantsEvents = tab === 'timeline' || tab === 'details' || maximized;
  useEffect(() => {
    if (!wantsEvents || eventsFetchedRef.current) return;
    eventsFetchedRef.current = true;
    let alive = true;
    getAdmissionEvents(admissionId).then((res) => {
      if (!alive) return;
      if (res.success) setEvents(res.data.events);
      else if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) setOutOfScope(true);
      else { setEvents([]); setEventsError(res.error || 'Failed to load timeline.'); }
    });
    return () => { alive = false; };
  }, [wantsEvents, admissionId]);

  // S2 AC3 — ONE ordered set (newest first, first 3 + "+n more"), shared by
  // the Details row list and the Change dialog's own list. Derived from the
  // `events` array with `useMemo` so the dialog never receives a freshly
  // rebuilt array on every render (a new reference in its `useMemo` deps).
  const dodEdits = useMemo(() => dodEditsFromEvents(events), [events]);
  const currentEdit = useMemo(() => currentDodEdit(events), [events]);
  const [dodHistoryExpanded, setDodHistoryExpanded] = useState(false);
  const dodHistory = useMemo(
    () => buildDodHistory(dodEdits, { showAll: dodHistoryExpanded }),
    [dodEdits, dodHistoryExpanded]);

  // ── Discharge dialog (one instance) + its own single-flight bill fetch ────
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [dischargeBill, setDischargeBill] = useState(null);
  const [authorisers, setAuthorisers] = useState([]); // O3 write-off "Authorised by" picker source
  const dischargeFetchedRef = useRef(false);

  const loadDischargeBill = useCallback(async () => {
    // Money hidden → the server would refuse the bill; never ask for it.
    if (moneyHidden) return { success: false };
    const res = await getRunningBill(admissionId);
    if (res.success) setDischargeBill(res.data);
    else if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) setOutOfScope(true);
    return res;
  }, [admissionId, moneyHidden]);

  useEffect(() => {
    if (!dischargeOpen || dischargeFetchedRef.current) return;
    dischargeFetchedRef.current = true;
    loadDischargeBill();
    // O3 — load the staff roster once for the write-off "Authorised by" picker.
    // fetchStaffList is single-flight + session-cached in staffApi, so this
    // cannot multiply requests across the ~14 pickers that share it.
    let alive = true;
    fetchStaffList().then((res) => {
      if (alive && res.success) setAuthorisers(buildAuthoriserOptions(res.data));
    });
    return () => { alive = false; };
  }, [dischargeOpen, loadDischargeBill]);

  const openDischarge = useCallback(() => {
    dischargeFetchedRef.current = false;
    setDischargeBill(null);
    setDischargeOpen(true);
  }, []);
  const closeDischarge = useCallback(() => setDischargeOpen(false), []);

  // DOD rule v3 (C1) — the optional `{discharge_at}` body the Initiate confirm
  // step builds rides straight through; `undefined` keeps the pre-v3 `{}`.
  const handleInitiate = useCallback((body) => initiateDischarge(admissionId, body), [admissionId]);
  // S1/S3 — the initiate response carries the confirmed date + its marker;
  // merging it locally re-labels the 4 chips and the Details row in the SAME
  // render (the parent's own refresh still runs, as before, for the rest).
  // FIX-17 — the SAME echo also carries the post-settlement `balance_paise`:
  // a CASHLESS stay has the insurer-approved amount APPLIED to the ledger at
  // initiate (the ONE INSURANCE_SETTLEMENT row is minted inside
  // `initiate_discharge`, before the row is read back). Forward that ONE key
  // through the existing single-source `patchAdmissionBalance` seam every
  // other money response already uses, so the KPI strip / desk row / rail
  // quote the new payable immediately AND the Bill panel's external-change
  // effect re-reads the ledger EXACTLY once (its `seenRef` diff bounds it —
  // no storm). `deposit_total_paise` is deliberately NOT forwarded: a
  // settlement is an accrual, not a deposit, so the row's NET held figure
  // does not move (and must never be crossed with the bill's GROSS one).
  const handleInitiated = useCallback((fullAdmission) => {
    if (fullAdmission) {
      applyPlanDischargeResponse(fullAdmission);
      if (fullAdmission.balance_paise != null && typeof onAdmissionPatched === 'function') {
        onAdmissionPatched({
          admission_id: admissionId,
          balance_paise: fullAdmission.balance_paise,
        });
      }
    }
    if (typeof onDischarged === 'function') onDischarged();
  }, [applyPlanDischargeResponse, admissionId, onAdmissionPatched, onDischarged]);
  // Cancel initiation (discharge-final FE-0) — the response (status back to
  // prior, dates/invoice number null) merges through the same seam as initiate,
  // then the desk's ONE refresh (`onDischarged` = refreshAfterWrite) reloads the
  // row + invalidates the search caches. No other request is added.
  const [cancelInitOpen, setCancelInitOpen] = useState(false);
  const closeCancelInit = useCallback(() => setCancelInitOpen(false), []);
  const handleCancelInitiation = useCallback((reason) => cancelInitiation(admissionId, reason), [admissionId]);
  const handleInitiationCancelled = useCallback((fullAdmission) => {
    if (fullAdmission) applyPlanDischargeResponse(fullAdmission);
    if (typeof onDischarged === 'function') onDischarged();
  }, [applyPlanDischargeResponse, onDischarged]);
  const handleFixCharges = useCallback((body) => fixChargesAfterDischargeDate(admissionId, body), [admissionId]);
  const handleClear = useCallback(() => clearBilling(admissionId), [admissionId]);
  const handleComplete = useCallback(async (body) => {
    const res = await completeDischarge(admissionId, body);
    if (typeof onDischarged === 'function') onDischarged();
    return res;
  }, [admissionId, onDischarged]);
  const handleCollect = useCallback(async (body) => {
    const res = await collectDeposit(admissionId, body);
    if (res.success) {
      await loadDischargeBill();
      // Patch the parent row from the RESPONSE so the KPI strip / deposit-low
      // banner update live and the Bill-tab remount-key flips (one ledger
      // refetch). Balance is always the server's authoritative post-write value.
      if (typeof onAdmissionPatched === 'function') {
        onAdmissionPatched({
          admission_id: admissionId,
          balance_paise: res.data.balance_paise_after,
          deposit_total_paise: res.data.deposit_total_paise_after,
          deposit_low: res.data.deposit_low,
        });
      }
    }
    return res;
  }, [admissionId, loadDischargeBill, onAdmissionPatched]);
  const handleReturnAdvance = useCallback(async (paymentId, body) => {
    const res = await returnAdvance(admissionId, paymentId, body);
    if (res.success) {
      await loadDischargeBill();
      // The return-advance envelope omits deposit_total_paise_after, so derive
      // the new NET held: a refund reduces held by exactly the refunded
      // magnitude (`refund.amount_paise` is the positive magnitude). Balance
      // still comes straight from the server's `balance_paise_after`.
      if (typeof onAdmissionPatched === 'function') {
        const heldBefore = Number(a.deposit_total_paise) || 0;
        onAdmissionPatched({
          admission_id: admissionId,
          balance_paise: res.data.balance_paise_after,
          deposit_total_paise: heldBefore - res.data.refund.amount_paise,
          deposit_low: res.data.deposit_low,
        });
      }
    }
    return res;
  }, [admissionId, loadDischargeBill, onAdmissionPatched, a.deposit_total_paise]);
  // D1: the discharge "print bill" action prints the unified Invoice at its
  // FINAL state (blob + in-app viewer), not the retired final-bill PDF.
  // bk-112 — returns the api's `{success, error}` result so DischargeDialog
  // can drive its own per-button loading state + surface a failed open.
  const handlePrintInvoice = useCallback(() => {
    return openInterimInvoicePdf(admissionId);
  }, [admissionId]);
  // Wave-3 FE-2 / F1-F2 — the consolidated Diagnostics Bill (DIAG/{fy}/#, own
  // document, GENERATE_IPD_BILL_PDF), routed through the ONE shared print
  // helper (confirm-before-mint, blob-error mapping). DischargeDialog's own
  // `runPrint` already generalizes the printing/error UI across BOTH its
  // Print-invoice and Generate-Diagnostics-bill buttons (step-2 + success
  // both call this SAME function) — that's pre-existing chrome, not a forked
  // copy of the hook's confirm/call logic. `generate()` resolves
  // `{success, error}` for `runPrint` to await, or `undefined` if the mint
  // warning is cancelled (treated as a no-op, same as any other cancel).
  //
  // `handlePrintLabBill(currentStatus)` takes the status DischargeDialog
  // passes in, not this component's own (possibly stale, FIX-3) `status`
  // prop — DischargeDialog flips its LOCAL `currentStatus` synchronously the
  // instant "Initiate discharge" succeeds, ahead of the parent desk's
  // refetch; falling back to this component's `status` here would risk
  // minting with NO confirmation shown in that window.
  const {
    generate: handlePrintLabBillHook,
    confirmModal: labBillConfirmModal,
  } = useDiagnosticsBillPrint(admissionId);
  const handlePrintLabBill = useCallback(
    (currentStatus) => handlePrintLabBillHook(currentStatus ?? status),
    [handlePrintLabBillHook, status],
  );
  // Follow-on bk-1254 (FIX-7-FE) — the post-complete-discharge "Print
  // discharge summary" button opens in the app's own viewer, never a new
  // tab, same as the two DischargeSummaryPanel sites + RunningBillPanel's
  // final-bill button. `title` is DischargeDialog's caller-supplied
  // 'Discharge summary'/'Death summary' (derived from the success outcome,
  // isDeathOutcome — this function does not re-derive it); the returned
  // `{success, error}` lets DischargeDialog's existing `runPrint` drive its
  // own busy state + `printError` banner, same as onPrintInvoice/onPrintLabBill.
  const handlePrintSummary = useCallback((title) => (
    openDischargeSummaryPdf(admissionId, { title })
  ), [admissionId]);
  const handlePrintDuesAck = useCallback(() => {
    window.open(duesAckPdfUrl(admissionId), '_blank', 'noopener,noreferrer');
  }, [admissionId]);
  const handlePrintWriteoffVoucher = useCallback(() => {
    window.open(writeOffVoucherUrl(admissionId), '_blank', 'noopener,noreferrer');
  }, [admissionId]);

  const dischargeBalancePaise = dischargeBill ? dischargeBill.balance_paise : a.balance_paise;
  const refundableDeposits = dischargeBill ? buildRefundableDeposits(dischargeBill.payments) : [];
  // bk-110 — `has_diagnostics` rides the RUNNING-BILL payload (dischargeBill),
  // not the admission row, so thread it explicitly into the DischargeDialog
  // (undefined until the bill loads → the dialog's disable fails OPEN).
  const dischargeHasDiagnostics = dischargeBill ? dischargeBill.has_diagnostics : undefined;

  // Route a taskbar action to its handler: discharge + write-off-print are
  // local; everything else routes to the desk's shared dialog mounts.
  const runAction = useCallback((item) => {
    setMoreOpen(false);
    if (item.kind === 'discharge') { openDischarge(); return; }
    if (item.kind === 'cancelInitiation') { setCancelInitOpen(true); return; }
    if (item.kind === 'printWriteoffVoucher') { handlePrintWriteoffVoucher(); return; }
    // Add charge — jump to the Bill tab (where the dialog lives) and bump the
    // token; RunningBillPanel opens its own existing 'add' dialog off it.
    if (item.kind === 'addCharge') { setTab('bill'); setAddChargeToken((t) => t + 1); return; }
    onAction(item.kind, a);
  }, [a, onAction, openDischarge, handlePrintWriteoffVoucher]);

  // One More-menu row (shared by the plain and the admin-gated path below).
  const renderMoreItem = (item) => (
    <button
      type="button"
      disabled={item.disabled}
      title={item.disabled ? item.disabledReason : undefined}
      className={item.disabled
        ? 'w-full text-left px-3 py-1.5 text-[12.5px] rounded-md text-slate-400 dark:text-slate-500 cursor-not-allowed'
        : item.tone === 'danger'
          ? 'w-full text-left px-3 py-1.5 text-[12.5px] rounded-md text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'w-full text-left px-3 py-1.5 text-[12.5px] rounded-md text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'}
      onClick={() => runAction(item)}
      data-testid={`taskbar-${item.kind}`}
    >
      {item.label}
      {item.disabled && (
        <span className="block text-[11px] text-slate-400 dark:text-slate-500 font-normal">{item.disabledReason}</span>
      )}
    </button>
  );

  // One task-bar button. The Discharge action (initiate / complete — their server
  // routes refuse a doctor outside the care team with 403 NOT_TREATING_DOCTOR) is
  // disabled for such a doctor WITH its reason: the tooltip, and the sentence on
  // its own line under the bar (`order-last basis-full` keeps it last in the wrap).
  const renderTaskbarButton = (item, notTreating = false) => {
    const blocked = notTreating && item.kind === 'discharge';
    return (
      <>
        <button
          type="button"
          className={`${btn[item.tone] || btn.default} disabled:opacity-60 disabled:cursor-not-allowed`}
          onClick={() => runAction(item)}
          disabled={blocked}
          title={blocked ? NOT_TREATING_DOCTOR_DISCHARGE_COPY : undefined}
          data-testid={`taskbar-${item.kind}`}
        >
          {item.label}
        </button>
        {blocked && (
          <span className="order-last basis-full text-[11.5px] text-slate-600 dark:text-slate-300" data-testid="taskbar-discharge-reason">
            {NOT_TREATING_DOCTOR_DISCHARGE_COPY}
          </span>
        )}
      </>
    );
  };

  const dayLabel = kpi.terminal
    ? (kpi.losDays != null ? `Stayed ${kpi.losDays + 1} d` : '—')
    : (kpi.losDays != null ? `Day ${kpi.losDays + 1}` : '—');
  const bedValue = kpi.bedLabel || (kpi.awaiting ? 'Awaiting' : '—');

  // S3/FE-B — Details stops being a tab once maximized (it becomes the
  // always-visible side column instead); Bill/Timeline/Discharge Summary
  // stay tabs in both modes.
  const tabKeys = [
    ...(moneyHidden ? [] : ['bill']),
    'diagnostics', 'timeline', 'summary',
    ...(maximized ? [] : ['details']),
  ];

  // Refused by the department wall: nothing of the admission is drawn.
  if (outOfScope) {
    return (
      <div className="p-6" data-testid="workspace-out-of-scope">
        <div role="alert" className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-5 text-amber-900 dark:text-amber-100">
          <h2 className="text-lg font-semibold mb-1">Admission not available</h2>
          <p className="text-sm"><OutOfDepartmentText /></p>
          {typeof onClose === 'function' && (
            <button
              type="button"
              onClick={onClose}
              className="mt-3.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold bg-teal-600 text-white hover:bg-teal-700"
              data-testid="workspace-out-of-scope-back"
            >
              ← Back to my list
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid={`workspace-${admissionId}`}>
      {/* ── Maximize / Close controls — always offered; "back to split" only
          while maximized (S3 owner-locked: TWO exits). ─────────────────── */}
      <div className="flex-none flex items-center justify-end gap-2.5 px-4 pt-3" data-testid="workspace-topline">
        {maximized && typeof onBackToSplit === 'function' && (
          <button
            type="button"
            onClick={onBackToSplit}
            className="text-[11.5px] font-medium text-slate-500 dark:text-slate-400 underline hover:text-slate-800 dark:hover:text-slate-100"
            data-testid="workspace-back-to-split"
          >
            ↩ Back to split view (keep patient selected)
          </button>
        )}
        {!maximized && typeof onMaximize === 'function' && (
          <button
            type="button"
            onClick={onMaximize}
            className="px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
            data-testid="workspace-maximize"
          >
            ⛶ Maximize view
          </button>
        )}
        {typeof onClose === 'function' && (
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-[12.5px] font-semibold bg-teal-600 text-white hover:bg-teal-700"
            data-testid="workspace-close"
          >
            ✕ Close · back to dashboard
          </button>
        )}
      </div>

      {/* ── Pinned top (KPI · header · taskbar · tabs) ────────────────────── */}
      <div className="flex-none p-4 border-b border-slate-200 dark:border-slate-700">
        {/* KPI strip — owner glance (single source: buildAdmissionKpis) */}
        {showKpiStrip && (
          <div className="flex gap-2 flex-wrap mb-3" data-testid="kpi-strip">
            {showBill && (
              <>
                <Kpi label={kpi.balance.label} value={kpi.balance.text} tone={kpi.balance.kind === 'owed' ? 'owe' : kpi.balance.kind === 'advance' ? 'advance' : ''} />
                <Kpi label="Deposits held" value={formatPaise(kpi.depositsHeldPaise)} />
              </>
            )}
            <Kpi label="Bed &amp; ward" value={bedValue} sub={kpi.wardName || null} />
            <Kpi label={kpi.terminal ? 'Length of stay' : 'Day of stay'} value={dayLabel} />
          </div>
        )}
        {showBill && kpi.depositLow && (
          <div className="flex items-center gap-2.5 px-3 py-2 mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-[12.5px]" data-testid="kpi-deposit-low-banner">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 flex-none" aria-hidden="true" />
            <span className="flex-1">
              <b>Deposit low</b>
              {kpi.depositShortfallPaise > 0 ? <> — the bill has outrun the deposit by <b className="tabular-nums">{formatPaise(kpi.depositShortfallPaise)}</b>.</> : null}
            </span>
            {canCollectDeposit && (
              <button type="button" onClick={() => onAction('deposit', a)} className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12px] font-semibold flex-none" data-testid="kpi-deposit-low-cure">+ Add deposit</button>
            )}
          </div>
        )}

        {/* Patient header */}
        <div className="text-lg font-bold text-slate-900 dark:text-white">{a.patient_name || a.patient_id}</div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <span className="text-[12.5px] text-slate-500 dark:text-slate-400">{a.ip_number}</span>
          <Chip
            text={ADMISSION_STATUS_LABELS[status] || status}
            tone={isInDischargeFlow(status)
              ? 'border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200'
              : 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'}
          />
          {a.is_mlc && <Chip text="MLC" tone="border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200" />}
          {/* admission-access M-FE-2 — the viewer's own place on the care team
              (server-derived `viewer_relationship`; absent for everyone else). */}
          <ViewerRelationshipChip relationship={a.viewer_relationship} you testId="workspace-viewer-chip" />
          {/* S4 (FE-3, Screen A) — read-only; editing lives on the "Plan
              discharge" taskbar button below, per stories Screen C
              ("Read-only here; clicking it does nothing"). */}
          {plannedDischargeChipTextValue && (
            <span data-testid="workspace-planned-discharge-chip">
              <Chip text={plannedDischargeChipTextValue} tone="border-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200" />
            </span>
          )}
          {/* [NEEDS-PO] copy — death-record slice, mockup Variant A state 3. */}
          {hasOpenDeathRecord(deathAdmission) && (
            <Chip text="● Deceased · Pending discharge" tone="border-red-400 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200" />
          )}
        </div>
        <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-1">
          {treatingDoctorLabel(a)} · {a.department_name || 'No department'} · {a.is_mlc ? 'MLC case' : 'Not an MLC case'}
        </div>
        {/* admission-access S4-AC4 — ONE quiet line says who sees the bill.
            Copy by permission, not role (C-7): a viewer who holds the bill
            slug but isn't this admission's primary doctor gets the doctor
            line; one without it gets the front-desk line. */}
        {moneyHidden && (
          <div className="flex items-center gap-1.5 mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400" data-testid="no-bill-hint">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-none">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>{canViewBill ? BILL_HINT_DOCTOR_COPY : BILL_HINT_NURSE_COPY}</span>
          </div>
        )}

        {/* ── Death record — quiet header link (Variant A, D40) while no
            record exists, or a prominent banner once one does. Re-renders
            off `deathAdmission` (local override merged onto the prop, S5 —
            no refetch). ────────────────────────────────────────────────── */}
        {!hasOpenDeathRecord(deathAdmission) && canRecordDeath && (
          <div className="mt-1.5">
            <button
              type="button"
              onClick={openRecordDeath}
              className="text-[12px] font-semibold text-teal-700 dark:text-teal-300 underline"
              data-testid="workspace-record-death-link"
            >
              {/* [NEEDS-PO] "+ Record death" vs "Record death…" vs "Death record" */}
              + Record death
            </button>
          </div>
        )}
        {hasOpenDeathRecord(deathAdmission) && (
          <div
            className="mt-2 flex items-start gap-2.5 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 text-[12.5px]"
            data-testid="workspace-death-banner"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-none" aria-hidden="true" />
            <span className="flex-1">
              {/* [NEEDS-PO] copy, pasted from the ratified mockup state 3. */}
              <b>Death recorded</b>
              {deathAdmission.cause_of_death.death_at ? ` · ${new Date(deathAdmission.cause_of_death.death_at).toLocaleString()}` : ''}
              {deathAdmission.cause_of_death.declaring_doctor_name ? ` · Declared by ${deathAdmission.cause_of_death.declaring_doctor_name}.` : '.'}
              {' '}The discharge type will be locked to Deceased.
            </span>
            <span className="flex gap-2 flex-none">
              <button
                type="button"
                onClick={openRecordDeath}
                className="px-2.5 py-1 rounded-md text-[11.5px] font-semibold bg-white dark:bg-slate-800 border border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/40"
                data-testid="workspace-death-edit"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setRemoveDeathConfirming(true)}
                className="px-2.5 py-1 rounded-md text-[11.5px] font-bold bg-white dark:bg-slate-800 border border-red-400 dark:border-red-600 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/40"
                data-testid="workspace-death-remove"
              >
                Remove
              </button>
            </span>
          </div>
        )}

        {/* ONE task bar (status-driven) */}
        <div className="flex items-center gap-2 flex-wrap mt-3" data-testid="workspace-taskbar">
          {taskbar.isClosed ? (
            <span className="text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="taskbar-closed">{taskbar.closedMessage}</span>
          ) : (
            <>
              {taskbar.primary.map((item) => (
                <React.Fragment key={item.key}>
                  {item.kind === 'discharge' ? (
                    // The role is read for this one action only (a null `viewer_relationship`).
                    <NonTreatingDoctorGate admission={a}>
                      {(notTreating) => renderTaskbarButton(item, notTreating)}
                    </NonTreatingDoctorGate>
                  ) : renderTaskbarButton(item)}
                  {/* DIAG-DISCOUNT v5 — peer entry to "Waiver / concession" on
                      the workspace task bar (the screen the owner actually
                      uses). Placed right beside it, not folded into
                      `deriveTaskbar`'s array: `DiagnosticWaiverDialog` is a
                      self-contained widget (FE-B) that owns its OWN entry
                      button + disabled-with-reason logic + modal — mounting
                      it here (once, non-embedded elsewhere on this screen)
                      preserves that behaviour instead of re-deriving it.
                      Same authority tier as the waiver (owner ruling —
                      GRANT_CONCESSION), so it rides the SAME visibility as
                      the concession button: absent when the viewer has no
                      concession capability, disabled-with-reason when they
                      do but can't use THIS action (non-admin, walk-in,
                      closed stay). */}
                  {item.key === 'concession' && (
                    <DiagnosticWaiverDialog
                      admission={a}
                      canRecord={canGrantConcession}
                      onRecorded={() => setWaiverRecordToken((t) => t + 1)}
                    />
                  )}
                </React.Fragment>
              ))}
              {/* S4/FE-2 — "Plan discharge" sits BESIDE "Initiate discharge"/
                  "Complete discharge" (build-plan FE-2, stories.md Screen A).
                  Rendered independently of `taskbar.primary` (not folded into
                  `deriveTaskbar`'s array, same rationale as the concession
                  peer above): its OR-gated perm (ADMIT_PATIENT OR
                  DISCHARGE_PATIENT) is wider than the discharge button's own
                  DISCHARGE_PATIENT-only gate, so a receptionist holding only
                  ADMIT_PATIENT must still see it even when "Initiate
                  discharge" itself is hidden. */}
              {canPlanDischargeNow && (
                <button
                  type="button"
                  className={btn.default}
                  onClick={openPlanDischarge}
                  data-testid="taskbar-planDischarge"
                >
                  Plan discharge
                </button>
              )}
              {taskbar.more.length > 0 && (
                <div className="relative">
                  <button type="button" className={btn.default} onClick={() => setMoreOpen((v) => !v)} data-testid="taskbar-more" aria-expanded={moreOpen}>More ▾</button>
                  {moreOpen && (
                    <div className="absolute right-0 z-30 mt-1 min-w-[180px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg p-1" data-testid="taskbar-more-menu">
                      {taskbar.more.map((item) => (
                        item.kind === 'cancelInitiation' && a.invoice_printed_before_complete ? (
                          <AdminOnlyAfterPrintGate key={item.key}>
                            {(locked) => renderMoreItem(locked ? { ...item, disabled: true, disabledReason: CANCEL_INITIATION_ADMIN_ONLY_COPY } : item)}
                          </AdminOnlyAfterPrintGate>
                        ) : <React.Fragment key={item.key}>{renderMoreItem(item)}</React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* S4/FE-2 — AC-I5 transient toast (setTimeout auto-dismiss, same idiom
            as DischargeSummaryPanel's snippet toast). Rendered here, not
            inside the dialog, because Clear plan CLOSES the dialog before the
            toast should be seen. */}
        {planDischargeToast && (
          <div className="mt-2 px-3 py-1.5 rounded-lg bg-slate-800 dark:bg-slate-700 text-white text-[12px] font-medium inline-block" data-testid="plan-discharge-toast">
            {planDischargeToast}
          </div>
        )}

        {/* Tab bar */}
        <div className="flex gap-1 mt-3" role="tablist">
          {tabKeys.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`px-3 py-1.5 text-[12.5px] font-semibold rounded-t-md border border-b-2 border-slate-200 dark:border-slate-700 ${
                tab === k
                  ? 'border-b-teal-500 bg-white dark:bg-slate-800 text-teal-700 dark:text-teal-300'
                  : 'border-b-transparent bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {k === 'bill' ? 'Bill' : k === 'diagnostics' ? 'Diagnostics' : k === 'timeline' ? 'Timeline' : k === 'summary' ? (isDeathOutcome(status) ? 'Death Summary' : 'Discharge Summary') : 'Details'}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab panels (Bill / Timeline / Discharge Summary; Details too when
          NOT maximized) — same content either way, only the surrounding
          layout differs (single column vs. side-by-side, see below). ────── */}
      {(() => {
        const tabPanels = (
          <>
            {tab === 'bill' && (
              showBill ? (
                <div className="p-4" data-testid={`running-bill-${admissionId}`}>
                  {/* Keyed by admission ONLY — never by the balance/deposit. When
                      the denorm balance/deposit moves (taskbar money action or WS
                      patch) the panel refetches its ledger IN PLACE via its own
                      balance-change effect, so an open dialog inside it (e.g.
                      AddChargeDialog "Save & add another") is NOT torn down by a
                      remount. Admission-switch already remounts the whole
                      AdmissionWorkspace (desk mounts it key={admission_id}). */}
                  <RunningBillPanel key={admissionId} admission={a} embedded openAddChargeToken={addChargeToken} onAdmissionPatched={onAdmissionPatched} diagnosticsRefreshToken={diagnosticsRefreshSignal} />
                </div>
              ) : (
                <div className="p-6 text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="bill-unavailable">
                  The running bill starts once the patient is admitted with an IP number.
                </div>
              )
            )}
            {tab === 'diagnostics' && (
              <DiagnosticsCard admission={a} onAction={onAction} diagnosticsRefreshToken={diagnosticsRefreshSignal} />
            )}
            {tab === 'timeline' && (
              <div className="p-4" data-testid="timeline-panel">
                {events == null && <div className="text-[13px] italic text-slate-400 dark:text-slate-500" data-testid="timeline-loading">Loading timeline…</div>}
                {Array.isArray(events) && events.length === 0 && <div className="text-[13px] italic text-slate-400 dark:text-slate-500">No timeline events yet</div>}
                {Array.isArray(events) && events.length > 0 && (
                  <ul className="list-none m-0 p-0">
                    {events.map((e) => {
                      const actorLabel = e.actor?.by_system ? 'System' : (e.actor?.name || null);
                      const timeLabel = e.occurred_at ? new Date(e.occurred_at).toLocaleString() : '';
                      // Wave-3 FE-3 — ADMISSION_INSURANCE_UPDATED carries a
                      // generic title ("Insurance details updated"); render
                      // the field old→new lines from `refs.changes` under it
                      // so the edit is readable without opening the admission.
                      const insuranceChangeLines = e.event_kind === ADMISSION_EVENT_KIND.INSURANCE_UPDATED
                        ? describeInsuranceEventChanges(e)
                        : [];
                      // death-record slice (FE-7, invariant 5/S7) — the three
                      // event kinds render off `e.title` (server-side, S6, no
                      // FE map); only ADMISSION_DEATH_UPDATED carries an
                      // edit diff, reusing the SAME `refs.changes` idiom.
                      const deathChangeLines = e.event_kind === ADMISSION_EVENT_KIND.DEATH_UPDATED
                        ? describeDeathRecordEventChanges(e)
                        : [];
                      return (
                        <li key={e.event_id} className="mb-2.5">
                          <div className="text-[13px] text-slate-700 dark:text-slate-200">{e.title}</div>
                          {e.refs && e.refs.cancel_initiation && (
                            <div className="text-[12px] text-slate-500 dark:text-slate-400" data-testid={`timeline-cancel-reason-${e.event_id}`}>
                              Reason: {e.refs.cancel_initiation.reason || '\u2014'}
                              {(e.actor && (e.actor.name || e.actor.by_system)) || e.occurred_at
                                ? ` (${[e.actor && (e.actor.by_system ? 'System' : e.actor.name), e.occurred_at && formatDischargeDateTimeIST(e.occurred_at)].filter(Boolean).join(', ')})`
                                : ''}
                            </div>
                          )}
                          {insuranceChangeLines.length > 0 && (
                            <ul className="list-none m-0 mt-0.5 pl-0" data-testid={`timeline-insurance-changes-${e.event_id}`}>
                              {insuranceChangeLines.map((line, i) => (
                                <li key={i} className="text-[12px] text-slate-500 dark:text-slate-400">{line}</li>
                              ))}
                            </ul>
                          )}
                          {deathChangeLines.length > 0 && (
                            <ul className="list-none m-0 mt-0.5 pl-0" data-testid={`timeline-death-changes-${e.event_id}`}>
                              {deathChangeLines.map((line, i) => (
                                <li key={i} className="text-[12px] text-slate-500 dark:text-slate-400">{line}</li>
                              ))}
                            </ul>
                          )}
                          <div className="text-[11.5px] text-slate-400 dark:text-slate-500">
                            {timeLabel}
                            {actorLabel && (timeLabel ? ' · ' : '')}
                            {actorLabel && `by ${actorLabel}`}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
            {tab === 'details' && !maximized && (
              <DetailGrid
                admission={payerAdmission}
                events={events}
                eventsError={eventsError}
                duesState={duesState}
                canEditInsurance={canEditInsurance}
                onInsuranceSaved={handleInsuranceSaved}
                patientDetailsRefreshToken={patientDetailsRefreshToken}
                careTeamCard={careTeamCard}
                dischargeDateRowValue={dischargeDateRowValue}
                dodHistory={dodHistory}
                dodEdits={dodEdits}
                currentDodEdit={currentEdit}
          onExpandDodHistory={() => setDodHistoryExpanded(true)}
                canManagePlanDischarge={canPlanDischargeNow}
                onChangePlanDischarge={openPlanDischarge}
                onClearPlanDischarge={handleClearPlanDischargeFromDetails}
              />
            )}
            {tab === 'summary' && (
              /* #9 follow-up (owner live-test) — the panel was already un-gated to
                 render while active (was: `hasFinalBill(status)`, terminal-only —
                 no entry point to author during the stay), but it was buried
                 inside the generic "Details" tab with nothing calling it out, so
                 the owner never found a way to CREATE a summary. Promoted to its
                 OWN top-level "Discharge Summary" tab (same eligibility as
                 before — active-or-final with an IP number, CANCELLED excluded)
                 so the create affordance is directly discoverable, not a scroll
                 away inside an unrelated tab. Reprint-locked once terminal — the
                 panel's OWN `locked` (untouched here), which FE-9 keys on
                 `isTerminal(status) && !isDeathOutcome(status)` so BE-7's
                 DECEASED save_summary carve-out stays reachable from this tab. */
              showDischargeSummary ? (
                <div className="p-4" data-testid={`discharge-summary-${admissionId}`}>
                  <DischargeSummaryPanel
                    admission={planDischargeAdmission}
                    dischargeType={a.discharge_type}
                    canPrint={canPrintDischargeDocs}
                    onAssignDoctor={assignDoctor}
                    patientDetailsRefreshToken={patientDetailsRefreshToken}
                  />
                </div>
              ) : (
                <div className="p-6 text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="discharge-summary-unavailable">
                  {status === ADMISSION_STATUS.CANCELLED
                    ? 'This admission was cancelled — there is no discharge summary to author.'
                    : 'The discharge summary is available once the patient is admitted with an IP number.'}
                </div>
              )
            )}
          </>
        );

        if (!maximized) {
          // FIX-5-FE — this used to be `overflow-y-auto` inside a parent
          // (`desk-workspace`) that AdmissionDeskPage force-clipped to
          // `calc(100vh - 260px)`, so EVERY tab (Bill included, not just
          // Summary) was squeezed into that box — the 16-field Discharge
          // Summary form only showed ~1 row above its own sticky footer.
          // AdmissionDeskPage no longer stretches this column to that fixed
          // height (see its `desk-split`/`desk-workspace` notes), so this
          // region now sizes to its own content and the PAGE scrolls — this
          // also means `position: sticky` on a descendant (the Summary
          // panel's Save/Print footer) resolves against the real viewport,
          // not a box that never actually scrolls.
          return (
            <div className="flex-1" data-testid="workspace-scroll">
              {tabPanels}
            </div>
          );
        }

        // S3/FE-B — maximized: Bill/Timeline/Summary (left, wider) + patient
        // Details ALWAYS visible (right) — no tab click needed to check the
        // bed/attender while reviewing the bill (mock2 rationale).
        // FIX-5-FE — same principle as the non-maximized branch above: no
        // per-column `overflow-y-auto`, so both columns size to their own
        // content (CSS grid stretches the shorter one to match) and the
        // page scrolls instead of either column clipping its content.
        return (
          <div className="flex-1 grid grid-cols-[1.7fr_1fr] min-h-0" data-testid="workspace-scroll-maximized">
            <div className="border-r border-slate-200 dark:border-slate-700" data-testid="workspace-scroll">
              {tabPanels}
            </div>
            <div className="bg-slate-50 dark:bg-slate-900" data-testid="workspace-details-side">
              <DetailGrid
                admission={payerAdmission}
                events={events}
                eventsError={eventsError}
                duesState={duesState}
                canEditInsurance={canEditInsurance}
                onInsuranceSaved={handleInsuranceSaved}
                patientDetailsRefreshToken={patientDetailsRefreshToken}
                careTeamCard={careTeamCard}
                dischargeDateRowValue={dischargeDateRowValue}
                dodHistory={dodHistory}
                dodEdits={dodEdits}
                currentDodEdit={currentEdit}
                canManagePlanDischarge={canPlanDischargeNow}
                onChangePlanDischarge={openPlanDischarge}
                onClearPlanDischarge={handleClearPlanDischargeFromDetails}
              />
            </div>
          </div>
        );
      })()}

      {dischargeOpen && (
        <DischargeDialog
          admission={deathAdmission}
          hasDiagnostics={dischargeHasDiagnostics}
          balancePaise={dischargeBalancePaise}
          refundableDeposits={refundableDeposits}
          canReturnAdvance={canReturnAdvance}
          canWriteOffDues={canWriteOffDues}
          canPrint={canPrintDischargeDocs}
          authorisers={authorisers}
          onClose={closeDischarge}
          onInitiate={handleInitiate}
          onInitiated={handleInitiated}
          onFixCharges={handleFixCharges}
          onClear={handleClear}
          onComplete={handleComplete}
          onCollectDeposit={handleCollect}
          onReturnAdvance={handleReturnAdvance}
          onPrintInvoice={handlePrintInvoice}
          onPrintLabBill={handlePrintLabBill}
          onPrintSummary={handlePrintSummary}
          onPrintDuesAck={handlePrintDuesAck}
          onAssignDoctor={assignDoctor}
        />
      )}
      {recordDeathOpen && (
        <RecordDeathDialog
          admission={deathAdmission}
          declaringDoctorOptions={declaringDoctorOptions}
          onClose={closeRecordDeath}
          onSave={handleSaveDeathRecord}
          onSaved={applyDeathResponse}
        />
      )}
      {cancelInitOpen && (
        <CancelInitiationDialog
          admission={planDischargeAdmission}
          onConfirm={handleCancelInitiation}
          onDone={handleInitiationCancelled}
          onClose={closeCancelInit}
        />
      )}
      {planDischargeOpen && (
        <PlanDischargeDialog
          admission={planDischargeAdmission}
          onClose={closePlanDischarge}
          onSave={handleSavePlanDischarge}
          onSaved={handlePlanDischargeSaved}
          onCleared={handlePlanDischargeCleared}
        />
      )}
      {removeDeathConfirming && (
        <ConfirmationModal
          isOpen
          variant="danger"
          title={`Remove death record for ${a.patient_name || a.patient_id}?`}
          // [NEEDS-PO] copy, pasted from the ratified mockup state 3 — consequences, not warnings.
          message={(
            <>
              {removeDeathError && (
                <div className="text-red-700 dark:text-red-300 font-semibold mb-2" data-testid="workspace-death-remove-error">{removeDeathError}</div>
              )}
              <ul className="list-disc pl-4 space-y-1 text-left">
                <li>The admission reopens as active — the discharge type is no longer locked to Deceased.</li>
                <li>A note is kept in the Timeline for reference — an internal audit trace, not a patient-facing document.</li>
                <li>Nothing has been issued yet at this stage (no certificate, no register entry) — this is safe to do before discharge.</li>
              </ul>
            </>
          )}
          ctaLabel={removeDeathBusy ? 'Removing…' : 'Remove record'}
          cancelLabel="Keep record"
          onConfirm={runRemoveDeathRecord}
          onCancel={() => (removeDeathBusy ? null : setRemoveDeathConfirming(false))}
        />
      )}
      {labBillConfirmModal}
    </div>
  );
}
